import { rawString } from '../components/Language/Language';
import type {
  Comment,
  Photo,
  ReasonItemData,
} from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import type { ReasonFieldVariables } from '../graphql/mutation/submit';
import {
  type Order,
  type OrderItem,
  type Reason,
  type Reasons,
} from '../graphql/query/getOrder';
import { queryClient } from '../queryClient';
import { type WorkflowData } from '../workflow';
import { QUERIES } from './constants';
import type { GetRetailerSettingsResult } from '../graphql/query/getRetailerSettings';
import { parseItemKey } from './itemKey';

export const getItemsFromOrder = (
  selectedItems: WorkflowData['items'],
  order: Order,
) => {
  const selectedItemIds = Object.keys(selectedItems || {});
  if (selectedItemIds.length === 0 || !order?.items) return [];

  return selectedItemIds
    .map((itemId) => {
      // First, try to find the item directly in order.items
      const directItem = order.items.find((i) => i.id === itemId);
      if (directItem) {
        return directItem;
      }

      // Handle virtual items using sourceItemId
      const itemData = selectedItems?.[itemId];
      const sourceItemId = itemData?.itemSelection?.sourceItemId;

      if (sourceItemId) {
        // Find the source item that this virtual item represents
        const sourceItem = order.items.find((i) => i.id === sourceItemId);
        if (sourceItem) {
          // Return the source item with the virtual item's ID for tracking
          return {
            ...sourceItem,
            id: itemId, // Use the virtual item's ID
          };
        }
      }

      // Handle compound keys using itemKey utility
      if (itemData?.itemSelection?.trackingNumber) {
        const { id: baseItemId, trackingNumber } = parseItemKey(itemId);

        // Only proceed if we actually have a tracking number in the parsed key
        if (trackingNumber) {
          const baseItem = order.items.find((i) => i.id === baseItemId);

          if (baseItem) {
            // Return the base item with the compound key ID for tracking
            return {
              ...baseItem,
              id: itemId, // Use the compound key ID
            };
          }
        }
      }

      return undefined;
    })
    .filter((item) => item !== undefined);
};

export const getItemDescription = ({
  item,
  quantity,
}: {
  item: Pick<OrderItem, 'variant'>;
  quantity?: number;
}) => {
  const fData = queryClient.getQueryData<GetRetailerSettingsResult>([
    QUERIES.GET_RETAILER_SETTINGS,
  ]);
  const itemAttributes = fData?.retailerSettings.features?.itemAttributes;
  if (!itemAttributes) return undefined;

  const { displayValueOnly, displayKeyValue } = itemAttributes;
  const variantInfo = (item.variant && item.variant.info) ?? [];

  const fragments: string[] = [];

  if (displayValueOnly?.length) {
    const valuesOnly = displayValueOnly.reduce<string[]>((acc, attr) => {
      const attrOnItem = variantInfo.find(({ name }) => name === attr);
      const attrValue = attrOnItem?.value;
      if (attrValue) {
        acc.push(attrValue);
      }
      return acc;
    }, []);

    if (valuesOnly.length) {
      fragments.push(valuesOnly.join(', '));
    }
  }

  if (displayKeyValue?.length) {
    const keyValues = displayKeyValue.reduce<string[]>((acc, attr) => {
      const attrOnItem = variantInfo.find(({ name }) => name === attr);
      const attrValue = attrOnItem?.value;
      if (attrValue) {
        const attrKeyLabel =
          rawString({
            langKey: `Global.item.attribute.${attr}`,
          }) ?? attr;
        acc.push(`${attrKeyLabel}: ${attrValue}`);
      }
      return acc;
    }, []);

    if (keyValues.length) {
      fragments.push(keyValues.join(', '));
    }
  }

  if (quantity && quantity > 1) {
    const quantityLabel = rawString({
      langKey: 'Global.item.attribute.quantity',
    });
    fragments.push(`${quantityLabel}: ${quantity}`);
  }

  return fragments.join(', ');
};

export const hasEligibleReason = (
  reasons: (Reason | null | undefined)[],
): boolean => {
  if (!reasons?.length) return false;

  const visited = new Set<string>();
  const stack: Reason[] = reasons.filter((r): r is Reason => r != null);

  while (stack.length) {
    const current = stack.pop()!;
    if (visited.has(current.code)) continue;
    visited.add(current.code);
    if (current.resolutions?.length) return true;
  }

  return false;
};

export const filterItemsByEligibility = (
  items: OrderItem[],
  isEligible = true,
) =>
  items.filter(({ reasons }) =>
    reasons ? isEligible === hasEligibleReason(reasons) : false,
  );

export type ExtractedReason = {
  reasons: Reasons;
  nestedReasons: Reasons;
  selectedRootReason: Reasons[number] | undefined;
  selectedNestedReason: Reasons[number] | undefined;
  selectedReason: Reasons[number] | undefined;
  isNestedReasonRequired: boolean;
  photoConfig?: {
    id?: string;
    enabled: boolean;
    required: boolean;
    max?: number;
  };
  commentConfig?: {
    metadataId?: string;
    enabled: boolean;
    required: boolean;
  };
  attestationConfig?: {
    metadataId?: string;
    enabled: boolean;
    required: boolean;
  };
  comments?: Comment;
  photos?: Photo[];
  attestation?: { checked?: boolean; metadataId?: string };
};

export const extractReasons = ({
  data,
  order,
}: {
  data: WorkflowData | null;
  order?: Order | null;
}) => {
  if (!order || !data) {
    return {};
  }
  const cartScopedReason = data.reason;
  const orderItems = getItemsFromOrder(data.items, order);

  if (!orderItems || orderItems.length === 0) {
    throw new Error('Cannot resolve without item data');
  }

  return orderItems.reduce(
    (acc: Record<OrderItem['id'], ExtractedReason>, orderItem) => {
      const { reason: { reasonId, nestedReasonId, comments, photos } = {} } =
        cartScopedReason ? data : (data.items?.[orderItem.id] ?? {});

      const { reasons } = orderItem;

      if (!reasons) {
        return acc;
      }

      const rootReasons = reasons.filter((r) => r?.parentId == null);
      const selectedRootReason = reasons.find(
        (r) => r?.code === reasonId && r?.parentId == null,
      );
      const nestedReasons = selectedRootReason
        ? reasons.filter((r) => r?.parentId === selectedRootReason.code)
        : [];
      const selectedNestedReason = nestedReasons.find(
        (r) => r?.code === nestedReasonId,
      );
      const selectedReason = selectedNestedReason || selectedRootReason;
      const commentField = selectedReason?.fields?.find(
        (f) => f?.type === 'COMMENT',
      );
      const photoField = selectedReason?.fields?.find(
        (f) => f?.type === 'PHOTO',
      );
      const attestationField = selectedReason?.fields?.find(
        (f) => f?.type === 'ATTESTATION',
      );
      const isNestedReasonRequired = Boolean(
        nestedReasons && nestedReasons.length > 0,
      );
      const commentConfig = {
        metadataId: commentField?.id ?? undefined,
        enabled: Boolean(commentField),
        required: Boolean(commentField && commentField.required),
      };
      const photoConfig = {
        id: photoField?.id ?? undefined,
        enabled: Boolean(photoField),
        required: Boolean(photoField && photoField.required),
        max: photoField?.max ?? undefined,
      };
      const attestationConfig = {
        metadataId: attestationField?.id ?? undefined,
        enabled: Boolean(attestationField),
        required: Boolean(attestationField && attestationField.required),
      };

      acc[orderItem.id] = {
        reasons: rootReasons,
        nestedReasons,
        selectedRootReason,
        selectedNestedReason,
        selectedReason,
        isNestedReasonRequired,
        commentConfig,
        photoConfig,
        attestationConfig,
        comments,
        photos,
        attestation:
          (cartScopedReason ? data : data.items?.[orderItem.id])?.reason
            ?.attestation ?? undefined,
      };

      return acc;
    },
    {},
  );
};

export const buildReasonFields = (
  reason: Pick<ReasonItemData, 'comments' | 'photos' | 'attestation'>,
): ReasonFieldVariables => {
  const fields: ReasonFieldVariables = [];

  if (reason.comments?.value) {
    fields.push({
      type: 'COMMENT',
      value: reason.comments.value,
      metadataId: reason.comments.metadataId,
    });
  }

  if (Array.isArray(reason.photos) && reason.photos.length > 0) {
    reason.photos.forEach((photo) => {
      fields.push({
        type: 'PHOTO',
        value: photo.id,
        metadataId: photo.metadataId,
      });
    });
  }

  if (reason.attestation?.checked === true) {
    fields.push({
      type: 'ATTESTATION',
      value: 'true',
      metadataId: reason.attestation?.metadataId,
    });
  }

  return fields;
};

export type ShipmentGroup = {
  shipmentIndex: number;
  trackingNumber: string;
  carrier: string;
  carrierService: string;
  shipDate: string;
  shipMethod: string;
  items: OrderItem[];
};

/**
 * Groups order items by their shipments for display purposes
 */
export const groupItemsByShipment = (order: Order): ShipmentGroup[] => {
  if (!order.shipments || order.shipments.length === 0) {
    // If no shipments, return all items in a single group
    return [
      {
        shipmentIndex: 0,
        trackingNumber: '',
        carrier: '',
        carrierService: '',
        shipDate: '',
        shipMethod: '',
        items: order.items || [],
      },
    ];
  }

  // Create a separate group for each shipment to preserve individual shipments
  // even when items appear in multiple shipments
  return order.shipments.map((shipment, index) => {
    const shipmentItemIds =
      shipment.itemsInfo?.map((itemInfo) => itemInfo.itemId) || [];
    const items =
      order.items?.filter((item) => shipmentItemIds.includes(item.id)) || [];

    return {
      shipmentIndex: index,
      trackingNumber: shipment.trackingNumber || '',
      carrier: shipment.carrier || '',
      carrierService: shipment.carrierService || '',
      shipDate: shipment.shipDate || '',
      shipMethod: shipment.shipMethod || '',
      items,
    };
  });
};
